<template>
    <div>
        <b-datatable
            ref="grid"
            :key="datatableKey"
            v-model="selectedItems"
            :columns="columns"
            :endpoint="listRoute"
            :hidden-columns="hiddenColumns"
            :filters-expanded="filtersExpanded"
            :initial-filters="initialFilters"
            class="patients-data-table"
            item-key="id"
            :grid-title="gridTitle"
            :has-bulk-operations="false"
            initial-sort="-memo_date"
            has-filter-selector
            :keep-filters-prefix="keepFiltersPrefix"
        >
            <template #filters="{ filters }">
                <v-col cols="4" class="pb-0">
                    <b-filter-input
                        v-model="filters.filters.memo_text"
                        :label="t('patients::grid.label.memo_text')"
                    ></b-filter-input>
                </v-col>

                <v-col cols="4" class="pb-0">
                    <b-filter-input v-model="filters.filters.name" :label="t('users::label.name')"></b-filter-input>
                </v-col>

                <v-col cols="4">
                    <b-filter-date
                        v-model="filters.filters.memo_date"
                        range
                        :label="t('patients::grid.label.memo_date')"
                        @clear="onCloseDateFilter(filters, 'memo_date')"
                    ></b-filter-date>
                </v-col>

                <v-col cols="4">
                    <b-filter-select
                        v-model="filters.filters.memo_type"
                        :items="filtersOptions.memo_type"
                        :label="t('patients::options.memo_type')"
                    >
                    </b-filter-select>
                </v-col>

                <v-col cols="4" class="pb-0">
                    <b-filter-input
                        v-model="filters.filters.dms_id"
                        :label="t('patients::grid.label.dms_id')"
                    ></b-filter-input>
                </v-col>

                <v-col cols="4" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.health_status"
                        :items="filtersOptions.health_status"
                        :label="t('patients::grid.label.health_status')"
                        :clearable="filters.filters.health_status !== null"
                    >
                    </b-filter-select>
                </v-col>

                <v-col cols="4" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.max_dispensing"
                        :items="filtersOptions.max_dispensing"
                        :label="t('patients::grid.label.max_dispensing')"
                        :clearable="filters.filters.max_dispensing !== null"
                    >
                    </b-filter-select>
                </v-col>

                <v-col cols="4" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.is_registered"
                        :items="filtersOptions.is_registered"
                        :label="t('patients::grid.label.is_registered')"
                        :clearable="filters.filters.is_registered !== null"
                    >
                    </b-filter-select>
                </v-col>

                <v-col cols="4" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.status"
                        :items="filtersOptions.status"
                        :label="t('patients::grid.label.status')"
                        :clearable="filters.filters.status !== null"
                    >
                    </b-filter-select>
                </v-col>
                <v-col cols="4" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.prohibited"
                        :items="filtersOptions.prohibited"
                        :label="t('patients::grid.label.prohibited')"
                        :clearable="filters.filters.prohibited !== null"
                    >
                    </b-filter-select>
                </v-col>
                <v-col cols="4" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.has_treatment_break"
                        :items="filtersOptions.treatmentBreakOptions"
                        :label="t('patients::grid.label.treatment_break')"
                        :clearable="filters.filters.has_treatment_break !== null"
                    >
                    </b-filter-select>
                </v-col>
            </template>

            <template #[`item.memo_blood_sample_color`]="{ item }">
                <div v-if="item.memo_blood_sample_color" :class="`blood-status ${item.memo_blood_sample_color}--text`">
                    <div :class="`status ${item.memo_blood_sample_color}--text`">
                        <v-icon>icon icon-blood-result</v-icon>
                    </div>
                    <span v-text="item.memo_blood_sample_color"></span>
                </div>
                <div v-else>
                    <span>{{ t('default::label.na') }}</span>
                </div>
            </template>

            <template #[`item.memo_type`]="{ item }">
                {{ t('patients::memos.types.' + item.memo_type) }}
            </template>

            <template #[`item.memo_text`]="{ item }">
                <div style="white-space: pre-wrap">{{ item.memo_text }}</div>
            </template>

            <template #[`item.name`]="{ item }">
                <div v-if="can('patients.get')" class="link" @click="$root.goTo(editItemUrl(item))">
                    {{ item.name }}
                </div>
                <div v-else>
                    {{ item.name }}
                </div>
            </template>

            <template #[`item.actions`]="{ item }">
                <div class="table-actions">
                    <v-tooltip v-if="can('patients.export-record-as-pdf')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="exportRecordAsPdf(item)"
                            >
                                <v-icon size="24">mdi-file-export-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('default::button.export_record_as_pdf') }}</span>
                    </v-tooltip>
                    <v-tooltip v-if="canStoreAE(item)" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(createAdverseEventUrl(item))"
                            >
                                <v-icon>mdi-text-box-plus-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('patients::button.create_adverse_event') }}</span>
                    </v-tooltip>
                    <v-tooltip
                        v-if="can('api.v1.patients.blood-results.update') && item.blood_result_editable == 1"
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(bloodResultEditItemUrl(item))"
                            >
                                <v-icon>mdi-pencil</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('users::button.edit') }}</span>
                    </v-tooltip>

                    <v-tooltip
                        v-if="can('api.v1.patients.blood-results.show') && item.blood_result_editable == 0"
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(bloodResultEditItemUrl(item))"
                            >
                                <v-icon>mdi-eye</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('patients::button.details') }}</span>
                    </v-tooltip>
                </div>
            </template>
        </b-datatable>
    </div>
</template>

<script>
export default {
    props: {
        listRoute: {
            type: String,
            required: true,
        },
        initialFilters: {
            type: Object,
            required: true,
        },
        hasPharmacySite: {
            type: Boolean,
            required: false,
            default: false,
        },
        gridTitle: {
            type: String,
            required: true,
        },
        filtersExpanded: {
            type: Boolean,
            required: false,
            default: true,
        },
        keepFiltersPrefix: {
            type: String,
            required: false,
            default: 'memos',
        },
    },
    data() {
        return {
            max_dispensing: 0,
            selectedItems: [],
            downloadingFile: false,
            datatableKey: 0,
            hiddenColumns: ['id'],
            ids: '',
            columns: [
                {
                    text: t('default::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('patients::grid.label.blood_status'),
                    value: 'memo_blood_sample_color',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('patients::grid.label.name'),
                    value: 'name',
                    sortable: true,
                    width: 200,
                },
                {
                    text: t('patients::grid.label.dms_id'),
                    value: 'dms_id',
                    sortable: true,
                    width: 100,
                },
                {
                    text: t('patients::grid.label.log_type'),
                    value: 'memo_type',
                    width: 150,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.memo_text'),
                    value: 'memo_text',
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.created_by'),
                    value: 'created_by',
                    width: 120,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.memo_date'),
                    value: 'memo_date',
                    sortable: true,
                    width: 110,
                },
                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: null,
                    width: 180,
                    align: 'end',
                },
            ],
            filtersOptions: {
                treatmentBreakOptions: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::treatment_break.true'),
                        value: 1,
                    },
                    {
                        text: t('patients::treatment_break.false'),
                        value: 0,
                    },
                ],
                prohibited: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::prohibited.true'),
                        value: 1,
                    },
                    {
                        text: t('patients::prohibited.false'),
                        value: 0,
                    },
                ],
                cnrd_date: [
                    {
                        text: t('patients::options.cnrd_date.all'),
                        value: null,
                    },
                    {
                        text: t('patients::options.cnrd_date.empty_date'),
                        value: 0,
                    },
                    {
                        text: t('patients::options.cnrd_date.not_empty_date'),
                        value: 1,
                    },
                ],
                status: [],
                max_dispensing: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::max_dispensing.yes'),
                        value: 1,
                    },
                    {
                        text: t('patients::max_dispensing.no'),
                        value: 0,
                    },
                ],
                validity: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::validity.valid'),
                        value: 'valid',
                    },
                    {
                        text: t('patients::validity.invalid'),
                        value: 'invalid',
                    },
                ],
                health_status: [],
                is_registered: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('consultants::options.registered.yes'),
                        value: 1,
                    },
                    {
                        text: t('consultants::options.registered.no'),
                        value: 0,
                    },
                ],
                dispensed: [
                    {
                        text: t('patients::options.dispensed.all'),
                        value: null,
                    },
                    {
                        text: t('patients::options.dispensed.yes'),
                        value: 1,
                    },
                    {
                        text: t('patients::options.dispensed.no'),
                        value: 0,
                    },
                ],
                memo_type: [],
            },
        };
    },
    created() {
        axios.get(route('api.v1.memos.config')).then((response) => {
            this.filtersOptions.memo_type = _.union(
                [
                    {
                        text: t('patients::status.all'),
                        value: null,
                    },
                ],
                response.data.memo_types && response.data.memo_types.length ? response.data.memo_types : []
            );
        });

        axios.get(route('api.v1.patients.config')).then((response) => {
            if (!response) {
                return;
            }
            this.filtersOptions.status = _.union(
                [
                    {
                        text: t('patients::status.all'),
                        value: null,
                    },
                ],
                response.data.statuses
            );

            this.filtersOptions.health_status = _.union(
                [
                    {
                        text: t('patients::status.all'),
                        value: null,
                    },
                ],
                response.data.health_statuses
            );
        });
    },
    methods: {
        async exportRecordAsPdf(item) {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('patients.memos.export-record-as-pdf', { patient: item.patient_id, memo: item.id }))
                .then((response) => {
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        createAdverseEventUrl(item) {
            return route('patients.get', {
                patient: item.patient_id,
                new_adverse_event: true,
                blood_result_id: item.related_record_id,
            });
        },
        canStoreAE(item) {
            const allowColors = ['red', 'amber', 'green'];
            if (item.has_adverse_event || item.rejected || !allowColors.includes(item.memo_blood_sample_color)) {
                return false;
            }
            return this.can('api.v1.patients.adverse-events.store') && !item.adverse_event_id;
        },
        changeTab() {
            this.$refs.grid;
            if (!this.$refs.grid) {
                return;
            }
            this.$refs.grid.switchTab();
        },
        editItemUrl(item) {
            return route('patients.get', { id: item.patient_id });
        },
        bloodResultEditItemUrl(item) {
            return route('patients.get', {
                patient: item.patient_id,
                load_lood_result: true,
                blood_result_id: item.related_record_id,
            });
        },
        itemUrl(item) {
            return route('patients.get', { id: item.id });
        },
        healthStatus(item) {
            if (item && item.health_status) {
                return _.map(item.health_status, (status) => {
                    return t('patients::health_status.' + status);
                }).join(', ');
            }
            return false;
        },
        onCloseDateFilter(filters, filter) {
            switch (filter) {
                case 'memo_date':
                    filters.filters.memo_date = null;
                    break;
                default:
                    break;
            }
        },
    },
};
</script>

<style></style>
